import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, startWith, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { Estimate, EstimateType, InsertPricingResource, InsertPricingResult, Organization } from '../../client-api.service';
import { AggregationType } from '../../shared/aggregate-util';
import { FileDownloadService } from '../../shared/tcc-ui/file-download.service';
import { convertFlatFileTextToArray, covertArrayToFlatFile, CSV_FORMAT } from '../../shared/tcc-ui/flat-file-util';
import { tapError } from '../../shared/tap-error-operator';
import { EstimateInputDebounceManager } from '../estimate-input-debounce-manager';
import { AreaEstimates } from '../models/area-estimates';
import { RevenueService } from '../revenue.service';
import { RevenueStateService, RootAreaState } from '../revenue-state.service';
import { RenewalsStateService } from './renewals-state.service';
import { RenewalMathService } from './renewal-math.service';
import { SubsManager } from 'src/app/shared/tcc-ui/subs-manager';
import { NotificationsService } from 'src/app/shared/tcc-ui/notifications';
import { ScrollService } from 'src/app/shared/tcc-ui/scroll.service';

interface ParsedCsvRow {
  cells: string[];
  lineNumber: number;
}

interface RenewalOfferCsvRow {
  propertyCode: string;
  unitType: string;
  space: string;
  renewal: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-renewals',
  templateUrl: './renewals.component.html',
  styles: [`
  .table .thead-dark th.sticky-top {
    /* this is a fix because stick doesn't keep the border so content shows behind the gap. */
    border-bottom: 0;
    border-top: 0;
    padding-bottom: calc(.3em + 2px);
    padding-top: calc(.3em + 1px);
  }
  .highlighted-text {
    /* #0d6efd is Bootstrap primary */
    color: #0d6efd;
    --bs-table-striped-color: #0d6efd;
  }
`]
})
export class RenewalsComponent implements AfterViewInit, OnDestroy, OnInit {

  private subsMgr = new SubsManager();

  isRenewalOfferUploading = false;
  isRenewalPricingSending = false;

  get isRenewalActionBusy(): boolean {
    return this.isRenewalOfferUploading || this.isRenewalPricingSending;
  }

  readonly hasSelections$ = this.renewalsState.selectedUnitIds$.pipe(
    map(x => x.length > 0),
    shareReplay(1)
  );

  readonly hasWarnings$ = this.renewalsState.unitTypes$.pipe(
    map(ut => ut.some(x => x.children?.some(y => this.hasUpdateWarning(y)))),
    shareReplay(1)
  );

  /** regulates updates of estimates. */
  readonly inputDebounceMgr = new EstimateInputDebounceManager();

  readonly isReadOnly$ = this.revState.isReadOnly$;

  @ViewChildren('offerElem') offerInputElemRefs: QueryList<ElementRef> | undefined;

  /** The current org. */
  readonly org$ = this.revState.org$;
  
  /** renewal math actions for the math menu component. */
  readonly renewalMathActions = this.renewalMathSvc.actions;

  /** The ids of the selected unit areas that are visible. */
  readonly selectedUnitIds$ = this.renewalsState.selectedUnitIds$;

  /** The count of the selected unit areas that are visible. */
  readonly selectedUnitCount$ = this.renewalsState.selectedUnitIds$.pipe(
    map(x => x.length),
    shareReplay(1)
  );

  readonly state$ = this.revState.areas$.pipe(
    filter((x): x is RootAreaState => !!x),
    map(({ areas }) => (areas && areas.length) ? 'ready' : 'loading'),
    startWith('loading'));

  /** Gets the estimates from the total area. */
  readonly totalEsts$ = this.renewalsState.total$.pipe(
    map((x) => x?.periods[this.renewalsState.tgtPeriodIndex]),
    shareReplay(1)
  );

  readonly unitTypeAggCategories: { aggType: AggregationType, label: string }[] = [
    { aggType: 'min', label: 'Min' },
    { aggType: 'max', label: 'Max' },
    { aggType: 'avg', label: 'Average' },
    { aggType: 'sum', label: 'Total' },
  ];

  readonly totalAggCategories = this.unitTypeAggCategories.filter(x => ['avg', 'sum'].includes(x.aggType));

  readonly tgtPeriodIndex = this.renewalsState.tgtPeriodIndex;

  readonly unitTypes$ = this.renewalsState.unitTypes$;

  @ViewChildren('utAnchor')
  unitTypeAnchors: QueryList<ElementRef> | undefined;

  readonly warningCount$ = this.renewalsState.unitTypes$.pipe(
    map(ut => ut
      .reduce((acc, cur) => acc + cur.children!.filter(x => this.hasUpdateWarning(x)).length, 0)),
    shareReplay(1)
  );

  constructor(
    private cd: ChangeDetectorRef,
    private fileDownloadSvc: FileDownloadService,
    private notifySvc: NotificationsService,
    private renewalMathSvc: RenewalMathService,
    private renewalsState: RenewalsStateService,
    private revenueSvc: RevenueService,
    public revState: RevenueStateService,
    private route: ActivatedRoute,
    private scrollSvc: ScrollService
  ) { }

  ngOnInit() {
    this.inputDebounceMgr.defaultAppliedOn = this.revState.periodSettings.periods[this.tgtPeriodIndex].value;
    this.inputDebounceMgr.defaultEstimateType = EstimateType.EstRenewalRate;

    this.subsMgr.addSub = this.route.params.pipe(
      filter((x): x is { orgId: string} => typeof x.orgId === 'string'),
      tap((x: { orgId: string }) => {
        this.revState.orgId = Number.parseInt(x.orgId, 10);
        this.renewalsState.filter = {};
      })
    ).subscribe();

    // this stream saves changes
    this.subsMgr.addSub = this.inputDebounceMgr.estimateChange$.pipe(
      tap(x => this.revState.enqueueEstimateSave(x.value.revAreaId!, {
        appliedOn: x.value.appliedOn!,
        estimateType: EstimateType.EstRenewalRate,
        estimateId: 0,
        value: x.value.value
      })),
      tapError(() => this.notifySvc.addError('Unable to save estimates.  Please refresh and try again.'))
    ).subscribe();

    this.subsMgr.addSub = this.renewalMathSvc.estimateUpdate$.pipe(
      tap(({ revAreaId, value }) => this.inputDebounceMgr.updateEstimate(value!, { revAreaId }, { forceUpdate: true }))
    ).subscribe();
  }

  ngAfterViewInit() {
    // if a fragment is set in the url before the table loads, no scrolling occurs.
    // this will wait until their are anchors loaded, and if so, will scroll if there is a fragment.
    this.subsMgr.addSub = combineLatest([this.unitTypeAnchors!.changes, this.route.fragment]).pipe(
      tap(([, fragment]) => {
        if (fragment) {
          const elem = this.unitTypeAnchors?.find(x => x.nativeElement.name === fragment);
          if (elem) {
            this.scrollSvc.scrollIntoView(elem);
          }
        }
      }),
      takeWhile(() => this.unitTypeAnchors?.length === 0)
    ).subscribe();
  }

  ngOnDestroy() {
    this.subsMgr.onDestroy();
    this.inputDebounceMgr.clear();
    this.revState.orgId = undefined;
  }

  /** Use at own risk in templates to force typing as AreaEstimates. */
  asAreaEstimates(x: unknown): x is AreaEstimates {
    return true;
  }
  /** Use at own risk in templates to force typing as AreaEstimates with meta required. */
  asAreaEstimatesWithMeta(x: unknown): x is AreaEstimates & Required<Pick<AreaEstimates, 'meta'>> {
    return true;
  }
  clearSelected() {
    this.revState.selectedIds.clear();
  }

  /** clears update warnings by forcing an update in the renewal estimate. */
  clearUpdateWarnings() {
    this.unitTypes$.pipe(
      take(1),
      tap(ut => {
        const units = ut.flatMap(x => x.children!.filter(y => this.hasUpdateWarning(y)));
        units.forEach(x => {
          const estimate: Estimate = {
            appliedOn: this.revState.periodSettings.cyJan.value,
            estimateType: EstimateType.EstRenewalRate,
            estimateId: 0,
            value: x.periods[this.tgtPeriodIndex].estRenewalRate!
          };
          this.revState.enqueueEstimateSave(x.revAreaId, estimate, undefined, true);
        });
      })
    ).subscribe();
  }

  hasUpdateWarning(area: AreaEstimates) {
    return area.warnings?.includes('updatesAfterEstRenewal') ?? false;
  }

  sendFilteredPricing(): void {
    combineLatest([
      this.org$.pipe(filter((org): org is Organization => org != null)),
      this.unitTypes$
    ]).pipe(
      take(1),
      tap(([org, unitTypes]) => {
        const propertyId = Number(org.entrataPropertyId);
        if (!Number.isInteger(propertyId) || propertyId <= 0) {
          this.notifySvc.addError('Unable to send pricing: the active property does not have a valid Entrata Property ID.');
          return;
        }

        // only get the filtered Unit Types
        const filteredUnits = unitTypes.flatMap(unitType => unitType.children ?? []);
        // of the filtered, get those that have a value
        const pricedUnits = filteredUnits.filter(unit => {
            const amount = unit.periods[this.tgtPeriodIndex].estRenewalRate;
            return amount != null && Number.isFinite(amount);
          });

        const unitsWithoutEstimateIds = pricedUnits.filter(unit => {
          const estimateId = unit.periods[this.tgtPeriodIndex].estRenewalRateEstimateId;
          return !Number.isInteger(estimateId) || estimateId! <= 0;
        });

        // make sure they have entrata ids
        if (unitsWithoutEstimateIds.length > 0) {
          this.notifySvc.addError(
            `Unable to send pricing: ${unitsWithoutEstimateIds.length} filtered renewals missing saved estimate ID.`
          );
          return;
        }

        const pricingResources = pricedUnits
          .map(unit => <InsertPricingResource>{
            propertyId,
            amount: unit.periods[this.tgtPeriodIndex].estRenewalRate!,
            revAreaEstimateId: unit.periods[this.tgtPeriodIndex].estRenewalRateEstimateId!,
            unitCode: unit.name
          });
        const skippedCount = filteredUnits.length - pricingResources.length; 

        if (pricingResources.length === 0) {
          this.notifySvc.addWarning('No renewal offers with pricing to send.');
          return;
        }

        const skippedMessage = skippedCount > 0
          ? ` ${skippedCount} blank renewal ${skippedCount === 1 ? 'offer' : 'offers'} will be skipped.`
          : '';
        if (!confirm(`Send pricing for ${pricingResources.length} filtered renewal ${pricingResources.length === 1 ? 'row' : 'rows'} to Entrata?${skippedMessage}`)) {
          return;
        }

        this.setRenewalPricingSending(true);
        this.subsMgr.addSub = this.revenueSvc.insertPricingBulk(pricingResources).pipe(
          map(results => pricingResources.map(resource => {
            const matches = (results || []).filter(result => result != null && result.revAreaEstimateId === resource.revAreaEstimateId);
            return matches.length === 1 ? matches[0] : <InsertPricingResult>{
              revAreaEstimateId: resource.revAreaEstimateId,
              status: 'Unknown',
              message: 'No unique result was returned for this offer.'
            };
          })),
          switchMap(results => results.some(result => this.pricingSendState(result) === 'success')
            ? this.revState.refreshCurrentOrganization().pipe(
              map(() => results),
              catchError(() => of(results))
            )
            : of(results)
          ),
          tap(results => this.notifyPricingSendResults(results, skippedCount)),
          catchError(() => {
            this.notifySvc.addError('Unable to confirm the bulk pricing results. Some offers may have been sent. Refresh and verify before resending.');
            return of([]);
          }),
          finalize(() => this.setRenewalPricingSending(false))
        ).subscribe();
      })
    ).subscribe();
  }

  downloadRenewalOffers(): void {
    combineLatest([
      this.org$.pipe(filter((org): org is Organization => org != null)),
      this.unitTypes$
    ]).pipe(
      take(1),
      tap(([org, unitTypes]) => {
        const headers = ['PropertyCode', 'UnitType', 'Space', 'Renewal'];
        const rows = unitTypes.flatMap(unitType => (unitType.children ?? []).map(space => [
          org.orgCode,
          unitType.name,
          space.name,
          space.periods[this.tgtPeriodIndex].estRenewalRate ?? ''
        ]));

        if (rows.length === 0) {
          this.notifySvc.addWarning('There are no renewal offers available to download.');
          return;
        }

        const csvText = '\uFEFF' + covertArrayToFlatFile([headers, ...rows], CSV_FORMAT); // this \uFEFF thing is helpful for Excel to know it's csv text
        this.fileDownloadSvc.startRawDownload(
          `${org.orgCode}_RenewalOffers.csv`,
          csvText,
          'text/csv;charset=utf-8;'
        );
      })
    ).subscribe();
  }

  onDirectionEvent(evt: KeyboardEvent) {
    // for ArrowLeft and Right, treat shift as resizing of selection area.
    const elem = <HTMLInputElement>evt.target;
    const offerInputElems: HTMLInputElement[] = (this.offerInputElemRefs)
      ? this.offerInputElemRefs.toArray().map(x => x.nativeElement)
      : [];
    const tgtIndex = offerInputElems.indexOf(elem);
    switch (evt.key) {
      case 'ArrowUp':
        if (tgtIndex > 0) {
          offerInputElems[tgtIndex - 1].select();
        }
        break;
      case 'ArrowDown':
      case 'Enter':
        if (tgtIndex < offerInputElems.length - 1) {
          offerInputElems[tgtIndex + 1].select();
        }
        break;
    }
  }

  onRenewalOfferFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);

    if (!file) {
      input.value = '';
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.notifySvc.addError('Invalid renewal offer file: select a .csv file.');
      input.value = '';
      return;
    }

    this.setRenewalOfferUploading(true);

    this.org$.pipe(
      filter((org): org is Organization => org != null),
      take(1)
    ).subscribe(org => {
      const reader = new FileReader();

      reader.addEventListener('load', () => {
        try {
          if (typeof reader.result !== 'string') {
            throw new Error('Unable to read the renewal offer CSV as text.');
          }

          const renewalOffers = this.parseAndValidateRenewalOfferCsv(reader.result, org.orgCode);
          this.subsMgr.addSub = this.renewalsState.postRenewalOffers(org.orgId, renewalOffers).pipe(
            tap(success => {
              if (success) {
                this.notifySvc.addSuccess(`${renewalOffers.length} renewal offers uploaded successfully.`);
              } else {
                this.notifySvc.addError('Unable to upload renewal offers. Please try again.');
              }
            }),
            tapError(() => this.notifySvc.addError('Unable to upload renewal offers. Please try again.')),
            finalize(() => this.setRenewalOfferUploading(false))
          ).subscribe({ error: () => undefined });
        } catch (error) {
          this.setRenewalOfferUploading(false);
          const message = error instanceof Error
            ? error.message
            : 'Invalid renewal offer CSV.';
          this.notifySvc.addError(message);
        }
      });

      reader.addEventListener('error', () => {
        this.setRenewalOfferUploading(false);
        this.notifySvc.addError('Unable to read the renewal offer CSV. Please try again.');
      });

      reader.addEventListener('abort', () => {
        this.setRenewalOfferUploading(false);
        this.notifySvc.addError('Reading the renewal offer CSV was canceled.');
      });

      reader.addEventListener('loadend', () => {
        input.value = '';
      });

      reader.readAsText(file);
    });
  }

  selectAll() {
    this.unitTypes$.pipe(
      take(1),
      tap(ut => this.revState.selectedIds.set(ut.flatMap(x => x.children || []).map(x => x.revAreaId)))
    ).subscribe();
  }

  toggleSelected(toToggle: AreaEstimates[] | AreaEstimates | undefined): void {
    if (toToggle) {
      const toToggleIds = (!Array.isArray(toToggle))
        ? [toToggle.revAreaId]
        : toToggle.map(x => x.revAreaId);
      this.revState.selectedIds.toggle(toToggleIds, false);
    }
  }

  trackByRevAreaId(_: number, area: AreaEstimates): number {
    return area.revAreaId;
  }

  updateFlag(area: AreaEstimates) {
    this.revState.enqueueMetaSave(area.revAreaId, area.meta!, this.revState.orgId);
  }

  updateItemsFlags(flag: string) {
    combineLatest([
      this.unitTypes$,
      this.selectedUnitIds$
    ]).pipe(
      take(1),
      tap(([ut, sui]) => {
        const selectedUnits = ut.flatMap(x => x.children || []).filter(y => sui.includes(y.revAreaId));
        selectedUnits.forEach(x => {
          const utMeta = x.meta;
          if (utMeta) {
            utMeta.flag = flag;
            this.revState.enqueueMetaSave(x.revAreaId, x.meta!, this.revState.orgId);
          }
        });
      })
    ).subscribe();
  }

  updateUnitEstimate(area: AreaEstimates, valueRaw: string) {
    this.inputDebounceMgr.updateRawEstimate(valueRaw, { revAreaId: area.revAreaId });
  }

  updateUnitEstimateForce(revAreaId: number) {
    this.inputDebounceMgr.updateForce({ revAreaId });
  }

  private parseAndValidateRenewalOfferCsv(csvText: string, activePropertyCode: string): RenewalOfferCsvRow[] {
    const expectedHeaders = ['PropertyCode', 'UnitType', 'Space', 'Renewal'];
    const parsedRows = this.parseCsv(csvText);

    if (parsedRows.length === 0) {
      throw new Error('Invalid renewal offer CSV: the file is empty.');
    }

    const headerRow = parsedRows[0];
    const headers = headerRow.cells.map((cell, index) => {
      const withoutBOM = index === 0 ? cell.replace(/^\uFEFF/, '') : cell; // this removes the Byte Order Mark character if it exists in the first cell of the header row - sometmes they exists
      return withoutBOM.trim();
    });

    if (headers.length !== expectedHeaders.length) {
      throw new Error(`Invalid renewal offer CSV at line ${headerRow.lineNumber}: expected exactly ${expectedHeaders.length} columns.`);
    }

    const invalidHeaderIndex = expectedHeaders.findIndex((header, index) => headers[index] !== header);
    if (invalidHeaderIndex !== -1) {
      throw new Error(
        `Invalid renewal offer CSV at line ${headerRow.lineNumber}: column ${invalidHeaderIndex + 1} must be ${expectedHeaders[invalidHeaderIndex]}.`
      );
    }

    if (parsedRows.length === 1) {
      throw new Error('Invalid renewal offer CSV: at least one data row is required.');
    }

    const activePropertyCodeNormalized = activePropertyCode.trim().toLowerCase();

    return parsedRows.slice(1).map(row => {
      if (row.cells.length < expectedHeaders.length - 1 || row.cells.length > expectedHeaders.length) {
        throw new Error(
          `Invalid renewal offer CSV at line ${row.lineNumber}: expected PropertyCode, UnitType, Space, and an optional Renewal value.`
        );
      }

      const values = row.cells.map(cell => cell.trim());
      if (values.length === expectedHeaders.length - 1) {
        values.push('');
      }

      const blankFieldIndex = values.slice(0, 3).findIndex(value => value.length === 0);
      if (blankFieldIndex !== -1) {
        throw new Error(
          `Invalid renewal offer CSV at line ${row.lineNumber}: ${expectedHeaders[blankFieldIndex]} is required.`
        );
      }

      const [propertyCode, unitType, space, renewal] = values;
      if (propertyCode.toLowerCase() !== activePropertyCodeNormalized) {
        throw new Error(
          `Invalid renewal offer CSV at line ${row.lineNumber}: PropertyCode ${propertyCode} does not match the active property ${activePropertyCode}.`
        );
      }

      if (renewal !== '' && !Number.isFinite(Number(renewal))) {
        throw new Error(
          `Invalid renewal offer CSV at line ${row.lineNumber}: Renewal must be a number without commas or dollar signs.`
        );
      }

      return { propertyCode, unitType, space, renewal };
    });
  }

  private parseCsv(csvText: string): ParsedCsvRow[] {
    return convertFlatFileTextToArray(csvText, CSV_FORMAT)
      .map((cells, index) => ({ cells, lineNumber: index + 1 }));
  }

  private setRenewalOfferUploading(isUploading: boolean): void {
    this.isRenewalOfferUploading = isUploading;
    this.cd.markForCheck();
  }

  private setRenewalPricingSending(isSending: boolean): void {
    this.isRenewalPricingSending = isSending;
    this.cd.markForCheck();
  }

  private pricingSendState(result: InsertPricingResult): 'success' | 'failed' | 'unknown' {
    const status = (result.status || '').toLowerCase();
    const validity = (result.isValid || '').toLowerCase();
    if (status === 'success' && validity === 'yes') {
      return 'success';
    }
    if (!status || status === 'unknown' || (status === 'success' && validity !== 'no')) {
      return 'unknown';
    }
    return 'failed';
  }

  private notifyPricingSendResults(results: InsertPricingResult[], skippedCount: number): void {
    const states = results.map(result => this.pricingSendState(result));
    const successCount = states.filter(state => state === 'success').length;
    const unknownCount = states.filter(state => state === 'unknown').length;
    const failedCount = results.length - successCount - unknownCount;
    const skippedMessage = skippedCount > 0
      ? ` ${skippedCount} blank renewal ${skippedCount === 1 ? 'offer was' : 'offers were'} skipped.`
      : '';

    if (unknownCount > 0) {
      this.notifySvc.addWarning(`${successCount} insert pricing rows sent, ${failedCount} failed, and ${unknownCount} have unconfirmed results. Verify unconfirmed offers before resending.${skippedMessage}`);
      alert(`${successCount} insert pricing rows sent, ${failedCount} failed, and ${unknownCount} have unconfirmed results. Verify unconfirmed offers before resending.${skippedMessage}`);
    } 
    else if (failedCount === 0) {
      this.notifySvc.addSuccess(`${successCount} insert pricing ${successCount === 1 ? 'row was' : 'rows were'} sent successfully.${skippedMessage}`);
      alert(`${successCount} insert pricing ${successCount === 1 ? 'row was' : 'rows were'} sent successfully.${skippedMessage}`);
    } 
    else if (successCount === 0) {
      this.notifySvc.addError(`Unable to send ${failedCount} insert pricing ${failedCount === 1 ? 'row' : 'rows'}.${skippedMessage}`);
      alert(`Unable to send ${failedCount} insert pricing ${failedCount === 1 ? 'row' : 'rows'}.${skippedMessage}`);
    } 
    else {
      this.notifySvc.addWarning(`${successCount} insert pricing ${successCount === 1 ? 'row was' : 'rows were'} sent and ${failedCount} ${failedCount === 1 ? 'row' : 'rows'} failed.${skippedMessage}`);
      alert(`${successCount} insert pricing ${successCount === 1 ? 'row was' : 'rows were'} sent and ${failedCount} ${failedCount === 1 ? 'row' : 'rows'} failed.${skippedMessage}`);
    }
  }

}
